"use client";

import { useState, useEffect } from "react";
import { ChevronRight, ArrowLeft } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { auth, db } from "../firebase";
import { ref, onValue, remove, update, get } from "../lib/firebase-database";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from "../lib/firebase-storage";
import LogoutConfirmationPopup from "./LogoutConfirmationPopup";
import DeleteReviewPopup from "./DeleteReviewPopup";
import { MobileBottomNav } from "./MobileBottomNav";
import { compressImage } from './action/ImageCompressor';


export default function ProfilePage() {
  const { user, isOwner, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  // Add these state declarations
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  const [userReviews, setUserReviews] = useState([]);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [villaNames, setVillaNames] = useState({});
  const [deleteReviewId, setDeleteReviewId] = useState(null);
  const [deleteVillaId, setDeleteVillaId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [phone, setPhone] = useState("");
  const [tempPhone, setTempPhone] = useState("");
  const [currentUserPhoto, setCurrentUserPhoto] = useState(user?.photoURL);
  const [notification, setNotification] = useState({ show: false, message: "", type: "" });
  const [username, setUsername] = useState(user?.displayName || "");
  const [tempUsername, setTempUsername] = useState(user?.displayName || "");
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showPhoneWarning, setShowPhoneWarning] = useState(false);
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [profileBackground, setProfileBackground] = useState("");
  const [tempProfileBackground, setTempProfileBackground] = useState("");
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);

  const backgroundOptions = [
    "https://cdn.pixabay.com/photo/2024/04/26/18/36/farm-house-8722429_1280.jpg",
    "https://cdn.pixabay.com/photo/2017/09/05/11/03/castle-2717222_1280.jpg",
    "https://cdn.pixabay.com/photo/2017/08/27/10/16/interior-2685521_1280.jpg",
  ];

  const getInitials = (name) => {
    const safeName = (name || "").trim();
    if (!safeName) return "VK";
    const parts = safeName.split(/\s+/).filter(Boolean);
    const letters = parts.slice(0, 2).map((part) => part[0].toUpperCase());
    return letters.join("");
  };

  useEffect(() => {
    if (!user) return;

    setIsLoading(true);
    const userRef = ref(db, `users/${user.uid}`);
    const unsubscribe = onValue(userRef, (snapshot) => {
      const userData = snapshot.val();
      if (userData?.photoURL) {
        setCurrentUserPhoto(userData.photoURL);
      }
      if (userData?.profileBackground) {
        setProfileBackground(userData.profileBackground);
        setTempProfileBackground(userData.profileBackground);
      } else {
        setProfileBackground(backgroundOptions[0]);
        setTempProfileBackground(backgroundOptions[0]);
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [user])

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!user) {
      setHasUnreadNotifications(false);
      return;
    }

    const notificationsRef = ref(db, `notifications/${user.uid}`);
    const unsubscribe = onValue(notificationsRef, (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        setHasUnreadNotifications(false);
        return;
      }

      const unread = Object.values(data).some((notification) => notification && notification.read !== true);
      setHasUnreadNotifications(unread);
    });

    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (currentUser) {
        // First check if user is an owner
        try {
          const villasRef = ref(db, "villas");
          const snapshot = await get(villasRef);
          const villasData = snapshot.val();
          const userIsOwner = villasData && Object.values(villasData).some(
            (villa) => villa.ownerEmail === currentUser.email
          );

          if (userIsOwner) {
            // For owners, get data from their property listing
            const ownerVilla = Object.values(villasData).find(
              (villa) => villa.ownerEmail === currentUser.email
            );

            if (ownerVilla) {
              // Update profile with villa owner data
              setUsername(ownerVilla.owner?.name || currentUser.displayName || "");
              setTempUsername(ownerVilla.owner?.name || currentUser.displayName || "");
              setPhone(ownerVilla.whatsappNumber || "");
              setTempPhone(ownerVilla.whatsappNumber || "");
              
              // Update photo if owner has one in property listing
              if (ownerVilla.owner?.image) {
                setCurrentUserPhoto(ownerVilla.owner.image);
                
                // Update auth profile if needed
                if (currentUser.photoURL !== ownerVilla.owner.image) {
                  try {
                    await currentUser.updateProfile({
                      photoURL: ownerVilla.owner.image
                    });
                    await currentUser.reload();
                  } catch (error) {
                    console.error("Error updating auth profile photo:", error);
                  }
                }
              }

              // Update database with owner info
              const userRef = ref(db, `users/${currentUser.uid}`);
              await update(userRef, {
                displayName: ownerVilla.owner?.name || currentUser.displayName,
                phone: ownerVilla.whatsappNumber,
                photoURL: ownerVilla.owner?.image || currentUser.photoURL,
                isOwner: true,
                updatedAt: new Date().toISOString()
              });
            }
          } else {
            // For regular users, get data from auth and database
            setUsername(currentUser.displayName || "");
            setTempUsername(currentUser.displayName || "");
            setCurrentUserPhoto(currentUser.photoURL);

            const userRef = ref(db, `users/${currentUser.uid}`);
            onValue(userRef, (snapshot) => {
              const userData = snapshot.val();
              if (userData) {
                if (userData.displayName) {
                  setUsername(userData.displayName);
                  setTempUsername(userData.displayName);
                }
                setPhone(userData.phone || "");
                setTempPhone(userData.phone || "");
                if (userData.photoURL) {
                  setCurrentUserPhoto(userData.photoURL);
                }
                if (userData.profileBackground) {
                  setProfileBackground(userData.profileBackground);
                  setTempProfileBackground(userData.profileBackground);
                }
              }
            });
          }
        } catch (error) {
          console.error("Error checking owner status:", error);
        }
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const villasRef = ref(db, "villas");
    onValue(villasRef, (snapshot) => {
      const villasData = snapshot.val();
      if (villasData) {
        const names = {};
        Object.entries(villasData).forEach(([villaId, villa]) => {
          names[villaId] = villa.name;
        });
        setVillaNames(names);
      }
    });
  
    return () => {
    };
  }, [user]);

  // Add this helper function
  const deleteOldProfilePhoto = async (photoURL) => {
    if (!photoURL) return;
    
    try {
      // Handle both Appwrite and Firebase URLs
      if (photoURL.includes('cloud.appwrite.io') && photoURL.includes('/files/')) {
        // Appwrite URL — extract file ID and delete
        const match = photoURL.match(/\/files\/([^/]+)\//);
        if (match) {
          const storage = getStorage();
          const oldPhotoRef = ref(storage, photoURL);
          oldPhotoRef._fileId = match[1];
          await deleteObject(oldPhotoRef);
        }
      } else if (photoURL.startsWith('https://firebasestorage.googleapis.com')) {
        // Legacy Firebase URL — skip (files already migrated)
        console.log('Skipping Firebase URL deletion (migrated)');
      }
      console.log('Old profile photo deleted successfully');
    } catch (error) {
      console.error('Error deleting old profile photo:', error);
    }
  };

  const handleSaveChanges = async () => {
    if (!user || isOwner) return; // Prevent owners from saving changes

    try {
      const updates = {};

      // Update username
      if (tempUsername !== username) {
        updates.displayName = tempUsername;
        try {
          await auth.currentUser.updateProfile({ displayName: tempUsername });
          // Force refresh to ensure new displayName is available
          await auth.currentUser.reload();
        } catch (error) {
          console.error("Error updating display name:", error);
        }
      }

      // Update phone number
      if (tempPhone !== phone) {
        if (phone) {
          setNotification({
            show: true,
            message: "Phone number cannot be changed once saved.",
            type: "error",
          });
          return;
        }
        setShowPhoneWarning(true);
        return;
      }

      // Profile background is stored locally (not in D1)
      if (tempProfileBackground && tempProfileBackground !== profileBackground) {
        setProfileBackground(tempProfileBackground);
      }

      // Update database — only send valid D1 columns
      if (Object.keys(updates).length > 0) {
        const userRef = ref(db, `users/${user.uid}`);
        await update(userRef, {
          ...updates,
          updatedAt: new Date().toISOString(),
        });
        
        setUsername(tempUsername);
        if (updates.profileBackground) {
          setProfileBackground(updates.profileBackground);
        }
        setIsEditing(false);
        setNotification({
          show: true,
          message: "Profile updated successfully",
          type: "success",
        });
      } else {
        setIsEditing(false);
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      setNotification({
        show: true,
        message: "Failed to update profile. Please try again.",
        type: "error",
      });
    }
  };

  const confirmPhoneSave = async () => {
    try {
      const userRef = ref(db, `users/${user.uid}`);
      await update(userRef, {
        phone: tempPhone,
        displayName: tempUsername,
        updatedAt: new Date().toISOString(),
      });

      setPhone(tempPhone);
      setUsername(tempUsername);
      if (tempProfileBackground) {
        setProfileBackground(tempProfileBackground);
      }
      setIsEditing(false);
      setShowPhoneWarning(false);
      setNotification({
        show: true,
        message: "Profile updated successfully. Note: Your phone number cannot be changed once saved.",
        type: "success",
      });
    } catch (error) {
      console.error("Error saving phone number:", error);
      setNotification({
        show: true,
        message: "Failed to save phone number. Please try again.",
        type: "error",
      });
    }
  };

  const handleLogoutClick = () => setShowLogoutConfirmation(true);

  const handleLogoutConfirm = () => {
    auth
      .signOut()
      .then(() => {
        setShowLogoutConfirmation(false);
        navigate("/");
      })
      .catch((error) => {
        console.error("Error signing out: ", error);
        setShowLogoutConfirmation(false);
      });
  };

  const handleLogoutCancel = () => setShowLogoutConfirmation(false);

  const handleDeleteReview = (villaId, reviewId) => {
    setDeleteVillaId(villaId);
    setDeleteReviewId(reviewId);
  };

  const confirmDeleteReview = () => {
    if (deleteVillaId && deleteReviewId) {
      const reviewRef = ref(db, `reviews/${deleteVillaId}/${deleteReviewId}`);
      remove(reviewRef)
        .then(() => {
          setDeleteReviewId(null);
          setDeleteVillaId(null);
          setNotification({
            show: true,
            message: "Review deleted successfully",
            type: "success",
          });
        })
        .catch((error) => {
          console.error("Error deleting review: ", error);
          setNotification({
            show: true,
            message: "Failed to delete review",
            type: "error",
          });
        });
    }
  };

  const cancelDeleteReview = () => {
    setDeleteReviewId(null);
    setDeleteVillaId(null);
  };

  const handleDeleteVilla = async (villaId) => {
    if (!villaId) return;

    try {
      const reviewsRef = ref(db, `reviews/${villaId}`);
      await remove(reviewsRef);

      const storage = getStorage();
      const villaImagesRef = ref(db, `villas/${villaId}/images`);
      const imagesSnapshot = await get(villaImagesRef);
      const imageUrls = imagesSnapshot.val();

      if (imageUrls) {
        await Promise.all(
          Object.values(imageUrls).map(async (url) => {
            const imageRef = storageRef(storage, url);
            await deleteObject(imageRef);
          })
        );
      }

      const villaRef = ref(db, `villas/${villaId}`);
      await remove(villaRef);

      setNotification({
        show: true,
        message: "Villa and associated data deleted successfully",
        type: "success",
      });
    } catch (error) {
      console.error("Error deleting villa:", error);
      setNotification({
        show: true,
        message: "Failed to delete villa",
        type: "error",
      });
    }
  };

  if (!user) {
    navigate("/");
    return null;
  }

  const displayedReviews = showAllReviews ? userReviews : userReviews.slice(0, 1);

  const goToAdminDashboard = () => {
    navigate("/admin");
    setShowProfileMenu(false);
  };

  const displayName = username || "Siddhes Patil";
  const avatarInitials = getInitials(displayName);

  return (
    <div className="min-h-screen bg-[#EDEFEF] pb-16">
      <header className="top-0 left-0 right-0 z-50">
        <div className="relative h-[220px]">
          <div
            className="absolute inset-0 bg-cover bg-center rounded-b-[32px]"
            style={{
              backgroundImage: `url('${profileBackground || backgroundOptions[0]}')`,
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-black via-black/60 to-black/20 rounded-b-[32px]"></div>
          </div>

          <div className="relative bg-black/30 py-3">
            <div className="flex justify-between items-center px-4">
              <button onClick={() => navigate(-1)} className="flex items-center text-white">
                <ArrowLeft className="h-8 w-8" />
              </button>
              <h1 className="text-2xl font-bold text-white ml-3">VillaKosh</h1>
              <button
                className="p-2"
                onClick={() => {}}
                aria-label="Notifications"
              >
                <div className="relative">
                  {hasUnreadNotifications && (
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></div>
                  )}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                    />
                  </svg>
                </div>
              </button>
            </div>
          </div>

          <div className="absolute left-1/2 -bottom-16 transform -translate-x-1/2">
            <div className="relative">
              {currentUserPhoto ? (
                <img
                  src={currentUserPhoto}
                  alt="Profile"
                  className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
                  onError={(e) => {
                    e.target.src = "/placeholder.svg";
                  }}
                />
              ) : (
                <div className="w-32 h-32 rounded-full border-4 border-white shadow-lg bg-black text-white flex items-center justify-center text-3xl font-semibold">
                  {avatarInitials}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {notification.show && (
        <div
          className={`fixed top-20 right-4 p-4 rounded-md shadow-lg ${
            notification.type === "success" ? "bg-green-500" : "bg-red-500"
          } text-white z-50 animate-fade-in-down`}
        >
          {notification.message}
        </div>
      )}

      <main className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-md p-6 max-w-2xl mx-auto">
          <div className="flex flex-col items-center mb-2">
            <div className="text-center w-full max-w-md mt-16">
              {!isEditing ? (
                <>
                  <h2 className="text-2xl font-bold mb-1">{displayName}</h2>
                  <p className="text-gray-600 mb-2">{user.email}</p>
                  <div className="flex items-center justify-center mt-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      className="h-4 w-4 mr-2 text-gray-500"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                    <span>{phone || "Add phone number"}</span>
                  </div>
                </>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                      Username
                    </label>
                    <input
                      id="username"
                      type="text"
                      value={tempUsername}
                      onChange={(e) => setTempUsername(e.target.value)}
                      placeholder="Enter your username"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                      Phone Number
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      value={tempPhone}
                      onChange={(e) => setTempPhone(e.target.value)}
                      placeholder="Enter your phone number"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                      disabled={!!phone} // Disable input if phone number is already set
                    />
                    {phone && (
                      <p className="text-sm text-gray-500 mt-1">
                        Phone number cannot be changed once saved.
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Profile Background
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      {backgroundOptions.map((option) => (
                        <button
                          key={option}
                          type="button"
                          onClick={() => setTempProfileBackground(option)}
                          className={`h-16 rounded-lg border-2 overflow-hidden transition-colors ${
                            tempProfileBackground === option ? "border-black" : "border-transparent"
                          }`}
                          aria-label="Select profile background"
                        >
                          <div
                            className="w-full h-full bg-cover bg-center"
                            style={{ backgroundImage: `url('${option}')` }}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-center gap-2 mb-8">
            {!isEditing ? (
              <button
                onClick={() => {
                  if (isOwner) {
                    setNotification({
                      show: true,
                      message: "Owner profile is synced with property listing",
                      type: "info",
                    });
                    return;
                  }
                  setIsEditing(true);
                  setTempPhone(phone);
                  setTempUsername(username);
                  setTempProfileBackground(profileBackground || backgroundOptions[0]);
                }}
                className={`px-6 py-2 ${
                  isOwner ? "bg-gray-400" : "bg-black hover:bg-gray-800"
                } text-white rounded-full transition-colors`}
                disabled={isOwner}
              >
                {isOwner ? "Profile Synced with Property" : "Edit Profile"}
              </button>
            ) : (
              <>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setTempPhone(phone);
                    setTempUsername(username);
                    setTempProfileBackground(profileBackground || backgroundOptions[0]);
                    setShowPhoneWarning(false);
                  }}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveChanges}
                  className="px-6 py-2 bg-black text-white rounded-full hover:bg-gray-800 transition-colors"
                >
                  Save Changes
                </button>
              </>
            )}
          </div>

          {(isAdmin || isOwner) && (
            <div className="grid gap-4 mb-8">
              {isOwner && (
                <Link to="/owner" className="bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition-colors">
                  <h3 className="text-lg font-semibold">Owner Dashboard</h3>
                  <p className="text-gray-600">Manage your properties</p>
                </Link>
              )}
              {isAdmin && (
                <Link to="/admin" className="bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition-colors">
                  <h3 className="text-lg font-semibold">Admin Dashboard</h3>
                  <p className="text-gray-600">Manage all properties</p>
                </Link>
              )}
            </div>
          )}

          {!isOwner && (
            <div className="bg-gray-50 p-4 rounded-lg mb-8">
              <h3 className="text-lg font-semibold mb-2">Are you a property owner?</h3>
              <p className="text-gray-600 mb-4">List your property </p>
              <Link
                to="/for-owner"
                className="inline-block bg-black text-white px-6 py-2 rounded-full hover:bg-gray-800 transition-colors"
              >
                List Your Property
              </Link>
            </div>
          )}

          <div className="mb-8">
            <div className="p-4 bg-gray-50 rounded-lg">
              <Link to="/wishlists" className="w-full block">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold mb-1">Saved Villas</h3>
                    <p className="text-sm text-gray-600">View your wishlist</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </div>
              </Link>
            </div>
          </div>

          <div className="space-y-4 mb-8">
            <h3 className="text-xl font-semibold">Support</h3>
            <div className="grid gap-4">
              <Link 
                to="/contact" 
                className="bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <h4 className="text-lg font-semibold">Contact Us</h4>
                <p className="text-gray-600">Get in touch with our team</p>
              </Link>

              <Link 
                to="" //about
                className="bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <h4 className="text-lg font-semibold">About Us(Upadating)</h4>
                <p className="text-gray-600">Learn more about VillaKosh and our mission</p>
              </Link>

              <Link 
                to="" //terms
                className="bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <h4 className="text-lg font-semibold">Terms & Conditions(Upadating)</h4>
                <p className="text-gray-600">Understand our terms of service</p>
              </Link>

              <Link 
                to="" // privacy
                className="bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <h4 className="text-lg font-semibold">Privacy Policy(Upadating)</h4>
                <p className="text-gray-600">Read our privacy policy</p>
              </Link>
            </div>
          </div>
        <button
            onClick={handleLogoutClick}
            className="w-full bg-red-50 p-4 rounded-lg text-left hover:bg-red-100 transition-colors"
          >
            <h3 className="text-lg font-semibold text-red-600">Logout</h3>
            <p className="text-red-600">Sign out of your account</p>
          </button>
        </div>
      </main>

      {showLogoutConfirmation && (
        <LogoutConfirmationPopup onConfirm={handleLogoutConfirm} onCancel={handleLogoutCancel} />
      )}

      {deleteReviewId && <DeleteReviewPopup onConfirm={confirmDeleteReview} onCancel={cancelDeleteReview} />}

      {showPhoneWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h2 className="text-xl font-semibold mb-4">Phone Number Warning</h2>
            <p className="text-sm text-gray-600 mb-4">
              Once you save your phone number, it cannot be changed. Are you sure you want to proceed?
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowPhoneWarning(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmPhoneSave}
                className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {isMobile && <MobileBottomNav user={user} />}
    </div>
  );
}

